import { AfterViewInit, Component, computed, DestroyRef, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatDialogModule } from '@angular/material/dialog';
import { FullCalendarComponent, FullCalendarModule } from '@fullcalendar/angular';
import { CalendarOptions, DatesSetArg, EventClickArg, EventInput } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import { Store } from '@ngrx/store';
import { take } from 'rxjs';
import { v4 as uuid } from 'uuid';

import { ConfirmComponent } from '../../../../../components/core/confirm/confirm.component';
import { SnackbarService } from '../../../../../core/services';
import { CalendarEventDialogComponent } from './calendar-event-dialog/calendar-event-dialog.component';
import type { CalendarEventDialogData, CalendarEventDialogResult } from './calendar-event-dialog/calendar-event-dialog.component';
import { CalendarEventActionsDialogComponent } from './calendar-event-actions-dialog/calendar-event-actions-dialog.component';
import type { CalendarEventActionsDialogResult } from './calendar-event-actions-dialog/calendar-event-actions-dialog.component';
import { CalendarIntegrationsDialogComponent } from './calendar-integrations-dialog/calendar-integrations-dialog.component';
import {
  CalendarEventCreateRequest,
  CalendarEventDeleteRequest,
  CalendarEventJoinRequest,
  CalendarEventJoinSuccess,
  CalendarEventsFetchRequest,
  CalendarEventUpdateRequest
} from '../../../../../store/calendar/calendar.actions';
import { selectCalendarEvents } from '../../../../../store/calendar/calendar.selectors';
import type { CalendarEvent } from '../../../../../store/calendar/calendar.models';
import { Actions, ofType } from '@ngrx/effects';
import { withBrandName } from '../../../../../core/config/branding';

const CALL_SESSION_STORAGE_PREFIX = 'meetrix.daily.session.';

@Component({
  selector: 'app-calendar-screen',
  imports: [FullCalendarModule, MatDialogModule],
  templateUrl: './calendar-screen.component.html',
  styleUrl: './calendar-screen.component.scss'
})
export class CalendarScreenComponent implements AfterViewInit {
  private readonly calendarRef = viewChild.required(FullCalendarComponent);
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly snackbar = inject(SnackbarService);
  private readonly actions$ = inject(Actions);
  private readonly locale = typeof navigator !== 'undefined' ? navigator.language : undefined;

  protected readonly monthLabel = signal(
    new Date().toLocaleDateString(this.locale, { month: 'long', year: 'numeric' })
  );
  protected readonly activeView = signal('timeGridWeek');
  protected readonly events = signal<CalendarEvent[]>([]);
  private readonly visibleRange = signal<{ start: Date; end: Date } | null>(null);
  private lastRequestedRangeKey: string | null = null;
  private calendarReady = false;
  protected readonly selectedDate = signal<Date>(new Date());
  protected readonly fullCalendarEvents = computed<EventInput[]>(() => this.expandEventsForRange(this.events(), this.visibleRange()));
  protected readonly agendaItems = computed(() => {
    const day = this.selectedDate();
    const start = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, 0, 0, 0);
    const end = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1, 0, 0, 0, 0);
    return this.expandEventsForRange(this.events(), { start, end }).sort((a, b) => a.start.localeCompare(b.start));
  });

  protected readonly calendarOptions = computed<CalendarOptions>(() => ({
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
    locale: this.locale,
    initialView: 'timeGridWeek',
    headerToolbar: false,
    events: this.fullCalendarEvents(),
    selectable: true,
    fixedWeekCount: true,
    dayMaxEvents: true,
    dateClick: (arg) => this.onDateClick(arg),
    eventClick: (arg) => this.onEventClick(arg),
    datesSet: (arg) => this.onDatesSet(arg),
    allDaySlot: true,
    slotMinTime: '00:00:00',
    slotMaxTime: '24:00:00',
    nowIndicator: true,
    eventDisplay: 'block',
  }));

  constructor() {
    this.store
      .select(selectCalendarEvents)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((events) => {
        this.events.set(events ?? []);
        this.syncRenderedEvents();
      });
  }

  ngAfterViewInit(): void {
    this.calendarReady = true;
    const api = this.calendarRef().getApi();
    const start = api.view?.activeStart;
    const end = api.view?.activeEnd;
    if (start && end) {
      this.visibleRange.set({ start, end });
      this.requestEventsForRange(start, end);
    }
    this.syncRenderedEvents();
  }

  protected showPreviousMonth(): void {
    this.calendarRef().getApi().prev();
  }

  protected showNextMonth(): void {
    this.calendarRef().getApi().next();
  }

  protected jumpToToday(): void {
    this.calendarRef().getApi().today();
    this.selectedDate.set(new Date());
  }

  protected switchView(view: 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay'): void {
    this.calendarRef().getApi().changeView(view);
  }

  protected openIntegrations(): void {
    this.dialog.open(CalendarIntegrationsDialogComponent, {
      width: '760px',
      maxWidth: 'calc(100vw - 24px)',
      maxHeight: 'calc(100dvh - 24px)',
      autoFocus: false,
      restoreFocus: false,
      disableClose: true,
    });
  }

  private onDateClick(arg: DateClickArg): void {
    this.calendarRef().getApi().select(arg.date);
    this.selectedDate.set(arg.date);

    const start = arg.date;
    const end = new Date(start.getTime() + 30 * 60 * 1000);

    this.openEventDialog({
      mode: 'create',
      start_at: start.toISOString(),
      end_at: end.toISOString(),
      all_day: !!arg.allDay,
    });
  }

  private onEventClick(arg: EventClickArg): void {
    const baseEventId = (arg.event.extendedProps as any)?.baseEventId as string | undefined;
    const eventId = baseEventId || arg.event.id;
    const existing = this.findEventById(eventId);
    if (!existing) {
      return;
    }

    this.openEventActions(existing);
  }

  private onDatesSet(arg: DatesSetArg): void {
    this.monthLabel.set(arg.view.title);
    this.activeView.set(arg.view.type);
    this.visibleRange.set({ start: arg.start, end: arg.end });
    this.syncRenderedEvents();
    this.requestEventsForRange(arg.start, arg.end);
  }

  private syncRenderedEvents(): void {
    if (!this.calendarReady) {
      return;
    }

    const api = this.calendarRef().getApi();
    api.removeAllEvents();
    api.addEventSource(this.fullCalendarEvents());
  }

  private requestEventsForRange(start: Date, end: Date): void {
    const startIso = start.toISOString();
    const endIso = end.toISOString();
    const rangeKey = `${startIso}|${endIso}`;
    if (this.lastRequestedRangeKey === rangeKey) {
      return;
    }

    this.lastRequestedRangeKey = rangeKey;
    this.store.dispatch(
      CalendarEventsFetchRequest({
        start: startIso,
        end: endIso,
      }),
    );
  }

  private openEventDialog(data: any): void {
    this.dialog
      .open(CalendarEventDialogComponent, {
        width: '600px',
        maxWidth: "95vw",
        maxHeight: 'calc(100dvh - 24px)',
        autoFocus: false,
        restoreFocus: false,
        disableClose: true,
        data: data as CalendarEventDialogData,
      })
      .afterClosed()
      .subscribe((result: CalendarEventDialogResult | undefined) => {
        if (!result || result.type === 'cancel') {
          return;
        }

        if (result.type === 'create') {
          this.store.dispatch(CalendarEventCreateRequest({ payload: result.payload, attachMeetrixLink: !!result.attachMeetrixLink }));
          console.log('Create event with payload:', result.payload, 'Attach Meetrix Link:', !!result.attachMeetrixLink);
          return;
        }

        if (result.type === 'update') {
          this.store.dispatch(CalendarEventUpdateRequest({ eventId: result.eventId, payload: result.payload }));
          if (result.attachMeetrixLink) {
            // this.store.dispatch(CalendarEventAttachMeetrixLinkRequest({ eventId: result.eventId }));
          }
          return;
        }

        if (result.type === 'delete') {
          this.store.dispatch(CalendarEventDeleteRequest({ eventId: result.eventId }));
        }
      });
  }

  protected onSelectedDateChange(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    const value = target?.value ?? '';
    const next = new Date(value);
    if (Number.isNaN(next.getTime())) {
      return;
    }
    this.selectedDate.set(next);
    this.calendarRef().getApi().gotoDate(next);
  }

  protected formatSelectedDate(): string {
    const day = this.selectedDate();
    const pad = (v: number) => String(v).padStart(2, '0');
    return `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;
  }

  protected createEventForSelectedDate(): void {
    const day = this.selectedDate();
    const start = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 9, 0, 0, 0);
    const end = new Date(start.getTime() + 30 * 60 * 1000);
    this.openEventDialog({
      mode: 'create',
      start_at: start.toISOString(),
      end_at: end.toISOString(),
      all_day: false,
    });
  }

  protected openAgendaEvent(instanceId: string): void {
    const baseEventId = instanceId.split(':')[0] ?? instanceId;
    const existing = this.findEventById(baseEventId);
    if (!existing) {
      return;
    }

    this.openEventActions(existing);
  }

  protected formatAgendaTime(iso: string): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  private expandEventsForRange(events: CalendarEvent[], range: { start: Date; end: Date } | null) {
    if (!range) {
      return [];
    }

    const rangeStart = range.start.getTime();
    const rangeEnd = range.end.getTime();
    const maxInstances = 500;
    const expanded: Array<{ id: string; title: string; start: string; end: string; allDay: boolean; extendedProps?: any }> = [];

    for (const event of events) {
      const frequency = (event.recurrence_frequency ?? '').trim().toLowerCase();
      const interval = Math.max(1, Number(event.recurrence_interval ?? 1));

      const baseStart = new Date(event.start_at);
      const baseEnd = new Date(event.end_at);
      const durationMs = Math.max(0, baseEnd.getTime() - baseStart.getTime());

      if (!frequency) {
        expanded.push({
          id: event.id,
          title: event.title,
          start: event.start_at,
          end: event.end_at,
          allDay: !!event.all_day,
        });
        continue;
      }

      const until = event.recurrence_until ? new Date(event.recurrence_until).getTime() : null;

      let cursor = new Date(baseStart);
      let guard = 0;
      while (cursor.getTime() < rangeStart && guard < 2000) {
        cursor = this.addRecurrence(cursor, frequency, interval);
        guard++;
      }

      guard = 0;
      while (cursor.getTime() < rangeEnd && expanded.length < maxInstances && guard < 2000) {
        const occStartMs = cursor.getTime();
        if (until !== null && occStartMs > until) {
          break;
        }

        const occEnd = new Date(occStartMs + durationMs);
        if (occEnd.getTime() > rangeStart && occStartMs < rangeEnd) {
          expanded.push({
            id: `${event.id}:${cursor.toISOString()}`,
            title: event.title,
            start: cursor.toISOString(),
            end: occEnd.toISOString(),
            allDay: !!event.all_day,
            extendedProps: { baseEventId: event.id },
          });
        }

        cursor = this.addRecurrence(cursor, frequency, interval);
        guard++;
      }
    }

    return expanded;
  }

  private addRecurrence(date: Date, frequency: string, interval: number): Date {
    const next = new Date(date);
    if (frequency === 'weekly') {
      next.setDate(next.getDate() + 7 * interval);
      return next;
    }
    if (frequency === 'monthly') {
      next.setMonth(next.getMonth() + interval);
      return next;
    }

    next.setDate(next.getDate() + interval);
    return next;
  }

  private openEventActions(event: CalendarEvent): void {
    this.dialog
      .open(CalendarEventActionsDialogComponent, {
        width: '420px',
        maxWidth: 'calc(100vw - 24px)',
        maxHeight: 'calc(100dvh - 24px)',
        autoFocus: false,
        restoreFocus: false,
        data: { event },
      })
      .afterClosed()
      .subscribe((result: CalendarEventActionsDialogResult | undefined) => {
        if (!result || result === 'cancel') {
          return;
        }

        if (result === 'join') {
          console.log('Joining event:', event);
          this.joinMeetingInApp(event);
          return;
        }

        if (result === 'delete') {
          this.confirmDeleteEvent(event);
          return;
        }

        this.openEventDialog(this.buildEditDialogData(event));
      });
  }

  private buildEditDialogData(event: CalendarEvent): CalendarEventDialogData {
    return {
      mode: 'edit',
      event: {
        id: event.id,
        event_type: event.event_type,
        title: event.title,
        description: event.description,
        location: event.location,
        start_at: event.start_at,
        end_at: event.end_at,
        all_day: event.all_day,
        attendee_user_ids: event.attendee_user_ids,
        attendee_emails: event.attendee_emails,
        attendee_external: event.attendee_external,
        recurrence_frequency: event.recurrence_frequency,
        recurrence_interval: event.recurrence_interval,
        recurrence_until: event.recurrence_until ?? null,
        meeting_url: event.meeting_url,
      },
      start_at: event.start_at,
      end_at: event.end_at,
      all_day: event.all_day,
    };
  }

  private findEventById(eventId: string | undefined): CalendarEvent | undefined {
    if (!eventId) {
      return undefined;
    }

    return this.events().find((event) => event.id === eventId);
  }

  private joinMeetingInApp(event: CalendarEvent): void {
    const eventId = event.id?.trim() ?? '';
    if (!eventId || typeof window === 'undefined') {
      return;
    }

    this.store.dispatch(CalendarEventJoinRequest({ eventId }));

    this.actions$.pipe(
      ofType(CalendarEventJoinSuccess),
      take(1),
    ).subscribe(({ payload }) => {
      const launchUrl = payload?.launch_url?.trim?.() ?? '';
      if (!launchUrl) {
        this.snackbar.showNotification('error', '', 'Unable to generate join link.', 'Close');
        return;
      }

      const sessionId = uuid();
      const sessionData = {
        id: sessionId,
        channelId: null,
        calendarEventId: eventId,
        launchUrl,
        title: event.title?.trim() || withBrandName('Meeting'),
        mode: 'video',
        userName: payload?.user_name?.trim?.() || undefined,
        createdAt: new Date().toISOString(),
      };

      window.localStorage.setItem(CALL_SESSION_STORAGE_PREFIX + sessionId, JSON.stringify(sessionData));
      this.openCallSession(sessionId);
    });

  }

  private openCallSession(sessionId: string): void {
    const basePath = `${window.location.origin}${window.location.pathname}`;
    const appCallUrl = `${basePath}#/call/${sessionId}`;
    const callWindow = window.open(appCallUrl, 'meetrix-call');
    if (callWindow && !callWindow.closed) {
      try {
        callWindow.opener = null;
      } catch {}
      return;
    }

    window.location.assign(appCallUrl);
  }

  private confirmDeleteEvent(event: CalendarEvent): void {
    const dialogRef = this.dialog.open(ConfirmComponent, {
      maxWidth: '420px',
      data: {
        message: `Are you sure you want to delete "${event.title}"?`,
        title: 'Delete Event',
      },
    });

    dialogRef.afterClosed().pipe(take(1)).subscribe((result) => {
      if (!result) {
        return;
      }

      this.store.dispatch(CalendarEventDeleteRequest({ eventId: event.id }));
    });
  }
}
